<script>
import { mapState, mapGetters } from 'vuex'

import Rating from '@/components/Rating'
import ProductsList from '@/components/chef/ProductsList'

import { CHEF_QUERY } from '@/graph/gql/chefs'
// import { followChef, unfollowChef } from '@/graph/mutations/chef'

export default {
  name: 'ChefView',
  components: { Rating, ProductsList },
  props: {
    preloaded: { type: Object, default: () => null }
  },
  apollo: {
    chef: {
      query: CHEF_QUERY,
      variables() {
        const { get } = this.lodash
        return {
          id: this.userId,
          date: get(this, 'delivery.deliverOn')
        }
      },
      result({ data }) {
        if (this.cartChef && this.cartChef.id !== data.chef.id) {
          this.$store.dispatch('cart/emptyCart')
        }

        this.$store.commit('cart/setChef', data.chef)

        // Event tracking
        if (this.sellerViewed) this.sellerViewed(data.chef)
      },
      skip() {
        return !this.userId
      }
    }
  },
  data: () => ({ chef: {}, isFollowed: false }),
  computed: {
    ...mapState({
      profileCompleted: state => state.user.profileCompleted,
      delivery: state => state.cart.delivery,
      cartChef: state => state.cart.chef
    }),
    ...mapGetters('cart', ['cartSize', 'subTotal']),

    userId() {
      const slug = this.$route.params.slug.split('-')
      return slug.pop()
    }
  },
  created() {
    // Event tracking
    const { once } = this.lodash
    this.sellerViewed = once(this._sellerViewed)

    if (this.preloaded) {
      this.chef = this.preloaded.chef
    }
  },
  methods: {
    // Go back
    goBack() {
      this.$router.replace('/')
    },

    goToCheckout() {
      if (this.profileCompleted) {
        this.$router.push({
          name: 'CheckoutView',
          params: { preloaded: { chef: this.chef } }
        })
      } else this.$router.push('/onboarding')
    },

    // Event tracking
    _sellerViewed(seller) {
      const data = seller || this.chef

      this.$amplitude.logEvent('Seller Viewed', data)
    }

    // followChef() {
    //   if (this.chef.isFollowed === true) return
    //   this.chef.isFollowed = true

    //   followChef(this.chef.id).catch(() => {
    //     this.chef.isFollowed = false
    //   })
    // },

    // unfollowChef() {
    //   if (this.chef.isFollowed === false) return
    //   this.chef.isFollowed = false

    //   unfollowChef(this.chef.id).catch(() => {
    //     this.chef.isFollowed = true
    //   })
    // }
  },
  metaInfo() {
    const { firstName, story } = this.chef || {}

    return {
      title: () => 'Order from home cook ' + firstName,
      meta: [{ name: 'description', content: () => firstName + ' ' + story }]
    }
  }
}
</script>

<template>
  <div class="chef-view container pa-0">
    <!-- Toolbar -->
    <v-app-bar flat fixed color="white" app>
      <v-btn icon @click="goBack">
        <v-icon color="black" size="18">$vuetify.icons.arrowBack</v-icon>
      </v-btn>
      <v-spacer />

      <!-- Follow -->
      <!-- <v-btn
        v-if="!$apollo.loading"
        icon
        color="black"
        @click="chef.isFollowed ? unfollowChef() : followChef()"
      >
        <v-icon color="black" size="18">
          {{ `$vuetify.icons.${chef.isFollowed ? 'heartFull' : 'heart'}` }}
        </v-icon>
      </v-btn> -->

      <!-- Share -->
      <!-- <v-btn icon>
        <v-icon color="black" size="18">$vuetify.icons.share</v-icon>
      </v-btn> -->

      <!-- Report -->
      <v-btn
        icon
        :to="{
          name: 'ReportChefView',
          params: { slug: chef && chef.slug, preloaded: { chef } }
        }"
      >
        <v-icon color="black" size="18">$vuetify.icons.flag</v-icon>
      </v-btn>
    </v-app-bar>

    <!-- Cart -->
    <transition name="fade" appear>
      <v-app-bar
        v-if="cartSize && !$apollo.loading"
        color="white"
        flat
        fixed
        bottom
        app
      >
        <v-btn
          rounded
          class="checkout-btn"
          depressed
          color="primary"
          @click="goToCheckout"
        >
          <v-badge left color="white" class="ml-3">
            <span slot="badge" class="primary--text font-weight-bold">
              {{ cartSize }}
            </span>
          </v-badge>

          <span class="ml-2">Checkout</span>
        </v-btn>
        <v-spacer />
        <div class="subtitle-1 secondary--text px-1">
          RM {{ subTotal | round(2, true) }}
        </div>
      </v-app-bar>
    </transition>

    <!-- Chef view page -->
    <div class="view-grid">
      <transition name="slide-up" appear>
        <div class="content-top px-0">
          <div>
            <!-- Chef profile -->
            <div>
              <v-skeleton-loader :loading="!chef" type="list-item-avatar">
                <v-list-item v-if="chef" class="px-6">
                  <v-list-item-content>
                    <v-list-item-title>
                      <!-- Name -->
                      <h1 class="display-1 font-weight-bold mb-2">
                        {{ chef.firstName }}
                      </h1>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      <!-- Halal-friendly -->
                      <span>
                        <v-chip
                          v-if="chef.agreements && chef.agreements.halal"
                          color="secondary"
                          text-color="white"
                          class="halal-friendly px-1 mb-3"
                          x-small
                        >
                          Halal-friendly
                        </v-chip>
                      </span>
                      <!-- Rating -->
                      <Rating :rating="chef.sellerRating" />
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <!-- Avatar -->
                  <v-list-item-avatar
                    class="mr-3 chef-avatar"
                    color="grey lighten-3"
                    size="80"
                  >
                    <v-img :src="chef.avatar" :lazy-src="chef.avatar" />
                  </v-list-item-avatar>
                </v-list-item>
              </v-skeleton-loader>

              <!-- Story -->
              <v-skeleton-loader
                :loading="!chef || !chef.description"
                transition="fade-transition"
                class="px-6 py-3"
                type="paragraph"
              >
                <p
                  class="body-2 secondary--text font-italic is-2 mb-1 chef-description"
                >
                  {{ chef.description }}
                </p>
              </v-skeleton-loader>
            </div>

            <v-divider />

            <!-- Products list -->
            <products-list
              v-if="chef && chef.sellerProducts"
              :products="chef.sellerProducts"
              class="my-2"
            />
            <div v-if="!chef || !chef.sellerProducts" class="py-2">
              <v-container class="pt-1 px-5" fluid>
                <v-row dense>
                  <v-col cols="6">
                    <v-skeleton-loader
                      :loading="!chef || !chef.sellerProducts"
                      type="image, table-cell"
                    >
                    </v-skeleton-loader>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.content-top
  margin-top: 56px
.checkout-btn
  padding: 0 18px !important
</style>

<style lang="sass">
.checkout-btn .v-badge__badge
  bottom: -10px !important
  right: 0 !important

.chef-view .products-loader
  .v-skeleton-loader__list-item-avatar-two-line
    height: 48px
  .v-skeleton-loader__avatar
    width: 48px
    height: 48px

.v-chip.halal-friendly
  font-size: 12px
  font-weight: 500
</style>
